import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import {
  ShieldCheck, UserPlus, FileText, Clock,
  CalendarCheck2, BriefcaseBusiness
} from 'lucide-react';
import './header.css';
import ImageService from '../images/imageService';

const Header = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('jwt'));
  const [userRole, setUserRole] = useState(localStorage.getItem('role') || '');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [menuStructure, setMenuStructure] = useState({});

  useEffect(() => {
    const handleStorageChange = () => {
      setIsLoggedIn(!!localStorage.getItem('jwt'));
      setUserRole(localStorage.getItem('role') || '');
    };

    window.addEventListener('storage', handleStorageChange);

    if (isLoggedIn) {
      api.get('/content/menuStructure')
  .then(res => setMenuStructure(res.data))
  .catch(err => console.error('Failed to fetch menu structure', err));
    }

    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isLoggedIn]);

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    setUserRole('');
    window.location.href = '/login/patient';
  };

  const getCreatorMenuItems = (role) => {
    const sections = [];

    if (["FRONT_DESK", "MANAGER", "ADMIN"].includes(role)) {
      sections.push({
        header: "Front Desk Tools",
        items: [{ path: "/register/insurance-patient", label: "Insurance Patient Registration", icon: UserPlus }]
      });
    }

    if (["MANAGER", "ADMIN"].includes(role)) {
      sections.push({
        header: "Manager Tools",
        items: [
          { path: "/register/office", label: "Register Office", icon: BriefcaseBusiness },
          { path: "/register/schedule", label: "Office Schedule Registration", icon: CalendarCheck2 },
          { path: "/register/authorization", label: "Patient Authorization", icon: FileText },
          { path: "/register/insurance", label: "Register Insurance", icon: ShieldCheck },
          { path: "/register/procedure-diagnosis", label: "Register Procedure/Diagnosis", icon: Clock },
          { path: "/register/promotion", label: "Add Promotion", icon: FileText },
          { path: "/register/point-item", label: "Add Point Inventory Item", icon: BriefcaseBusiness }
        ]
      });
    }

    // Content Creator - visible to all roles
    sections.push({
      header: "Content Creator",
      items: [
        { path: "/content-creator", label: "Manage Content", icon: FileText }
      ]
    });

    return sections;
  };

  const logoUrl = ImageService.getImageUrl("header", "newuphysicaltherapylogo.png");

  return (
    <>
      <div className="topBar">
        <div className="leftInfo">
          <span>📍 1835 Richmond Rd, Staten Island, NY 10306</span>
          <span>📞 718-502-5271</span>
          <span>📧 newupt@yahoo.com</span>
        </div>
        <div className="rightIcons">
          <a href="https://m.facebook.com/100063959434089/" target="_blank" rel="noreferrer">
            <img src={ImageService.getImageUrl("header", "facebooklogo.png")} alt="Facebook" className="socialIcon" />
          </a>
          <a href="https://www.instagram.com/new_u_physicaltherapy/" target="_blank" rel="noreferrer">
            <img src={ImageService.getImageUrl("header", "instagramlogo.png")} alt="Instagram" className="socialIcon invert" />
          </a>
        </div>
      </div>

      <div className="headerWrapper">
        <header className="headerBar">
          <img src={logoUrl} alt="Logo" className="logoImg" />
          <div className="siteTitle">
            <h1>NEW U PHYSICAL THERAPY</h1>
            <p><em>Let Your Pain Be Our Concern</em></p>
          </div>
          <div className="logoSpace"></div>
        </header>

        <nav className="navbar">
          <ul className="navlinks">
            <li className="navitem"><Link to="/" className="navlink">Home</Link></li>
            <li className="navitem"><Link to="/office" className="navlink">Office</Link></li>
            <li className="navitem"><Link to="/staff" className="navlink">Staff</Link></li>
            <li className="navitem"><Link to="/insurance" className="navlink">Insurance</Link></li>
            <li className="navitem"><Link to="/appointments" className="navlink">Appointments</Link></li>

            {isLoggedIn && userRole !== 'PATIENT' && (
              <li className="navitem"
                  onMouseEnter={() => setActiveDropdown('creator')}
                  onMouseLeave={() => setActiveDropdown(null)}>
                <span className="navlink">Creator</span>
                {activeDropdown === 'creator' && (
                  <ul className="dropdownMenu">
                    {getCreatorMenuItems(userRole).flatMap(section =>
                      section.items.map(({ path, label, icon: Icon }) => (
                        <li key={path}>
                          <Link to={path} className="dropdownLink"><Icon size={16} /> {label}</Link>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </li>
            )}

            {isLoggedIn ? (
              <>
                <li className="navitem">
                  <Link to={userRole === 'PATIENT' ? "/dashboard/patient" : "/dashboard/doctor"} className="navlink">
                    Dashboard
                  </Link>
                </li>
                <li className="navitem">
                  <span className="navlink" onClick={handleLogout}>Logout</span>
                </li>
              </>
            ) : (
              <>
                <li className="navitem"
                    onMouseEnter={() => setActiveDropdown('register')}
                    onMouseLeave={() => setActiveDropdown(null)}>
                  <span className="navlink">Register</span>
                  {activeDropdown === 'register' && (
                    <ul className="dropdownMenu">
                      <li><Link to="/register/patient" className="dropdownLink">Patient</Link></li>
                      {userRole === 'ADMIN' && (
                        <li><Link to="/register/staff" className="dropdownLink">Staff</Link></li>
                      )}
                    </ul>
                  )}
                </li>
                <li className="navitem"
                    onMouseEnter={() => setActiveDropdown('login')}
                    onMouseLeave={() => setActiveDropdown(null)}>
                  <span className="navlink">Login</span>
                  {activeDropdown === 'login' && (
                    <ul className="dropdownMenu">
                      <li><Link to="/login/patient" className="dropdownLink">Patient Login</Link></li>
                      <li><Link to="/login/doctor" className="dropdownLink">Doctor Login</Link></li>
                    </ul>
                  )}
                </li>
              </>
            )}

            {isLoggedIn && (
              <li className="navitem"
                  onMouseEnter={() => setActiveDropdown('register')}
                  onMouseLeave={() => setActiveDropdown(null)}>
                <span className="navlink">Register</span>
                {activeDropdown === 'register' && (
                  <ul className="dropdownMenu">
                    <li><Link to="/register/patient" className="dropdownLink">Patient</Link></li>
                    {userRole === 'ADMIN' && (
                      <li><Link to="/register/staff" className="dropdownLink">Staff</Link></li>
                    )}
                  </ul>
                )}
              </li>
            )}
          </ul>
        </nav>
      </div>
    </>
  );
};

export default Header;
